import { useRef, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Camera } from '@phosphor-icons/react';
import { CAUSE_LABEL, DOCUMENT_KIND, DOCUMENT_KIND_LABEL, LIMITS, isViewableFile, limitInMb, type DocumentKind } from '@helpdesk/shared';
import { assertDocumentSize, deleteJson, postJson, uploadFile } from '../api/client.ts';
import { machineQuery } from '../api/queries.ts';
import type { MachineDetail } from '../api/types.ts';
import { MachineLightBadge, PriorityBadge, StatusBadge } from '../components/badges.tsx';
import { FileViewer, downloadUrl, type ViewerFile } from '../components/file-viewer.tsx';
import { ActionNote, Button, Card, EmptyState, ErrorNote, Input, Modal, Notice, Select, Spinner, Tabs, errorMessage } from '../components/ui.tsx';
import { fmtDate, fmtDateTime } from '../lib/format.ts';
import { resizePhoto } from '../lib/image.ts';
import { useAction } from '../lib/use-action.ts';
import { MachineForm } from './machines.tsx';

/**
 * Maschinenakte (F37) = Ticketverlauf je Maschine: Stammdaten, Dokumente,
 * Historie, Stillstand je Monat. Melder sehen Stammdaten, Ampel und Tickets
 * nur als Nummer/Status/Priorität/Zeit, Instandhalter die Tickets anderer Werke ebenso.
 */
export function MachinePage() {
  const { id } = useParams({ from: '/app/maschinen/$id' });
  const machine = useQuery(machineQuery(id));
  const qc = useQueryClient();
  const [tab, setTab] = useState('stamm');
  const [editing, setEditing] = useState(false);
  const { run, busy, error, setError } = useAction();

  if (machine.isPending) return <Spinner />;
  if (machine.isError) return <ErrorNote error={machine.error} />;
  const m = machine.data;
  const refresh = () => qc.invalidateQueries({ queryKey: ['machine', id] });

  const tabs = [
    { id: 'stamm', label: 'Stammdaten' },
    { id: 'docs', label: `Dokumente (${m.documents.length})` },
    { id: 'tickets', label: `Tickets (${m.tickets.length})` },
    ...(m.permissions.seeHistory ? [{ id: 'kpi', label: 'Kennzahlen' }] : []),
  ];

  return (
    <div className="space-y-5">
      <Link to="/maschinen" search={{}} className="ad-link inline-flex min-h-10 items-center gap-1.5 text-[15px] font-medium no-underline">
        <ArrowLeft size={18} /> Alle Maschinen
      </Link>
      <header className="ad-card flex flex-wrap items-start gap-5">
        <MachinePhotoBox m={m} onChange={refresh} onError={setError} />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="ad-section-title">
            {m.site.name} · {m.area.name}
          </div>
          <h1 className="text-[26px] normal-case">{m.name}</h1>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono">{m.inventoryNo}</span>
            <MachineLightBadge light={m.light} large />
            {m.status === 'decommissioned' ? <span className="ad-badge bg-steel-200">Stillgelegt {fmtDate(m.decommissionedAt)}</span> : null}
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            {m.status === 'active' ? (
              <Link to="/melden" search={{ machineId: m.id }} className="ad-button-primary ad-button-sm">
                Störung melden
              </Link>
            ) : null}
            {m.permissions.canEdit ? (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                Bearbeiten
              </Button>
            ) : null}
          </div>
        </div>
        <div className="w-[132px] shrink-0 text-center">
          <img src={`/api/machines/${m.id}/qr.svg`} alt={`QR-Code ${m.qrCode}`} className="w-full border border-[var(--border-default)] bg-white p-1" />
          <div className="mt-1 font-mono text-sm">{m.qrCode}</div>
          {m.permissions.canEdit ? (
            <button
              type="button"
              className="mt-1 cursor-pointer text-[13px] text-arnold-red"
              disabled={busy}
              onClick={() => {
                if (!window.confirm('Neuen QR-Code erzeugen? Der alte leitet noch 12 Monate weiter.')) return;
                void run(async () => {
                  await postJson(`/api/machines/${m.id}/rotate-qr`);
                  await refresh();
                });
              }}
            >
              Etikett verloren?
            </button>
          ) : null}
        </div>
      </header>

      {error ? <ErrorNote error={error} /> : null}
      <div className="overflow-x-auto">
        <Tabs items={tabs} active={tab} onChange={setTab} />
      </div>

      {/* Bleibt beim Reiterwechsel eingehängt: Ein laufender Upload verlöre sonst seine Sperre und seine Fehlermeldung. */}
      <Documents machineId={m.id} docs={m.documents} canEdit={m.permissions.canUploadDocuments} active={tab === 'docs'} onChange={refresh} />

      {tab === 'stamm' ? (
        <div className="grid gap-4 md:grid-cols-2">
          <Card title="Stammdaten">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
              {[
                ['Hersteller', m.manufacturer],
                ['Typ', m.model],
                ['Seriennummer', m.serialNo],
                ['Baujahr', m.yearBuilt],
                ['Verantwortlich', m.responsible?.name],
                ['Infor-Anlage', m.inforAssetNo],
              ].map(([k, v]) => (
                <div key={k as string} className="contents">
                  <dt className="text-[var(--text-muted)]">{k}</dt>
                  <dd className="m-0">{(v as string | number | null | undefined) ?? '-'}</dd>
                </div>
              ))}
            </dl>
          </Card>
          <div className="space-y-4">
            {m.safetyNote ? <Notice tone="warn">Sicherheitshinweis: {m.safetyNote}</Notice> : null}
            {m.notes ? <Card title="Notizen">{m.notes}</Card> : null}
            {m.permissions.canDecommission ? (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  const reactivate = m.status !== 'active';
                  if (!window.confirm(reactivate ? 'Maschine wieder aktivieren?' : 'Maschine stilllegen? Die Akte bleibt erhalten.')) return;
                  try {
                    await postJson(`/api/machines/${m.id}/decommission`, { reactivate });
                    await refresh();
                  } catch (err) {
                    setError(err);
                  }
                }}
              >
                {m.status === 'active' ? 'Stilllegen' : 'Wieder aktivieren'}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      {tab === 'tickets' ? (
        m.tickets.length ? (
          <div className="divide-y divide-[var(--border-default)] border border-[var(--border-default)] bg-white">
            {m.tickets.map((t) => (
              <div key={t.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3">
                {t.canOpen ? (
                  <Link to="/tickets/$number" params={{ number: t.number }} className="ad-link font-mono">
                    {t.number}
                  </Link>
                ) : (
                  <span className="font-mono">{t.number}</span>
                )}
                <PriorityBadge priority={t.priority} short />
                <StatusBadge status={t.status} />
                {'title' in t && t.title ? <span className="min-w-0 flex-1 truncate">{t.title}</span> : <span className="flex-1" />}
                {'causeCategory' in t && t.causeCategory ? <span className="text-[13px] text-[var(--text-muted)]">{CAUSE_LABEL[t.causeCategory]}</span> : null}
                <span className="text-[13px] text-[var(--text-muted)]">{fmtDateTime(t.createdAt)}</span>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState title="Noch keine Tickets" />
        )
      ) : null}

      {tab === 'kpi' ? (
        <Card title="Stillstand je Monat (12 Monate)">
          {m.downtime.length ? (
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="ad-th">Monat</th>
                  <th className="ad-th">Störungen mit Stillstand</th>
                  <th className="ad-th">Stunden</th>
                </tr>
              </thead>
              <tbody>
                {m.downtime.map((d) => (
                  <tr key={d.month}>
                    <td className="ad-td ad-num">{d.month}</td>
                    <td className="ad-td ad-num">{d.count}</td>
                    <td className="ad-td ad-num">{d.hours}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-[var(--text-muted)]">Kein Stillstand erfasst.</p>
          )}
        </Card>
      ) : null}

      <Modal open={editing} title="Maschine bearbeiten" onClose={() => setEditing(false)}>
        <MachineForm
          initial={m}
          onSaved={() => {
            setEditing(false);
            void refresh();
          }}
          onCancel={() => setEditing(false)}
        />
      </Modal>
    </div>
  );
}

/** Foto zum Wiedererkennen: Klick vergrößert; die Instandhaltung lädt hoch, ersetzt, entfernt. */
function MachinePhotoBox({ m, onChange, onError }: { m: MachineDetail; onChange: () => Promise<void>; onError: (e: unknown) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(false);
  const canEdit = m.permissions.canEdit;
  if (!m.photo && !canEdit) return null;

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      await uploadFile(`/api/machines/${m.id}/photo`, await resizePhoto(file), 'foto.jpg');
      await onChange();
    } catch (err) {
      onError(err);
    }
    setBusy(false);
  };
  const remove = async () => {
    if (!window.confirm('Foto der Maschine entfernen?')) return;
    try {
      await deleteJson(`/api/machines/${m.id}/photo`);
      await onChange();
    } catch (err) {
      onError(err);
    }
  };

  return (
    <div className="w-[160px] shrink-0 space-y-1 text-center">
      {m.photo ? (
        <button type="button" className="block w-full cursor-zoom-in" onClick={() => setView(true)}>
          <img src={m.photo.thumbUrl} alt={`Foto: ${m.name}`} className="aspect-[4/3] w-full border border-[var(--border-default)] object-cover" />
        </button>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => input.current?.click()}
          className="flex aspect-[4/3] w-full cursor-pointer flex-col items-center justify-center gap-1 border border-dashed border-[var(--border-strong)] bg-[var(--surface-subtle)] text-[13px] text-[var(--text-muted)]"
        >
          <Camera size={28} aria-hidden />
          {busy ? 'Wird hochgeladen …' : 'Foto hinzufügen'}
        </button>
      )}
      {canEdit && m.photo ? (
        <div className="flex justify-center gap-3 text-[13px]">
          <button type="button" className="cursor-pointer text-steel-700 underline" disabled={busy} onClick={() => input.current?.click()}>
            {busy ? 'Wird hochgeladen …' : 'Ersetzen'}
          </button>
          <button type="button" className="cursor-pointer text-arnold-red" onClick={() => void remove()}>
            Entfernen
          </button>
        </div>
      ) : null}
      {canEdit && !m.photo ? <p className="text-[13px] text-[var(--text-muted)]">Nur die Maschine, keine Personen.</p> : null}
      {canEdit ? (
        <input
          ref={input}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            void upload(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      ) : null}
      <FileViewer
        file={view && m.photo ? { url: m.photo.url, filename: `${m.name}.jpg`, contentType: 'image/jpeg' } : null}
        onClose={() => setView(false)}
      />
    </div>
  );
}

type Doc = {
  id: string;
  kind: DocumentKind;
  title: string;
  url: string | null;
  fileUrl: string | null;
  filename: string | null;
  contentType: string | null;
  validFrom: string | null;
  createdAt: string;
};

/** Eingerollte Meldungen bleiben frei von der festen Kopfzeile. */
const BELOW_HEADER = 'scroll-mt-[calc(4rem+env(safe-area-inset-top))]';

function Documents({ machineId, docs, canEdit, active, onChange }: { machineId: string; docs: Doc[]; canEdit: boolean; active: boolean; onChange: () => Promise<void> }) {
  const [kind, setKind] = useState<DocumentKind>('manual');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [view, setView] = useState<ViewerFile | null>(null);
  const [uploading, setUploading] = useState(false);
  // Der Fehler steht an seiner Zeile: Das Formular liegt bei langer Liste am Handy außerhalb des Bildschirms.
  const [removing, setRemoving] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const save = useAction();
  const remove = useAction();
  // Ist der Reiter beim Scheitern zu, muss die Meldung außerhalb stehen, sonst gilt das Dokument als gespeichert.
  const failed = save.error ?? remove.error;

  const confirmKind = () =>
    kind !== 'inspection_report' || window.confirm('Prüfbericht dauerhaft speichern? Er bleibt, solange die Maschine existiert, und lässt sich später nicht entfernen.');
  // Ein Prüfbericht ist unumkehrbar, darum gilt die Art nur für dieses eine Dokument.
  const resetForm = () => {
    setKind('manual');
    setTitle('');
  };

  const addLink = () =>
    save.run(async () => {
      if (!confirmKind()) return;
      await postJson(`/api/machines/${machineId}/docs/link`, { kind, title, url });
      resetForm();
      setUrl('');
      await onChange();
    });
  const addFile = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    await save.run(async () => {
      assertDocumentSize(file);
      if (!confirmKind()) return;
      await uploadFile(`/api/machines/${machineId}/docs`, file, file.name, { kind, title: title || file.name.replace(/\.[^.]+$/, '') });
      resetForm();
      await onChange();
    });
    setUploading(false);
  };

  return (
    <>
      {failed && !active ? (
        <ActionNote revealOn={failed} className={BELOW_HEADER}>
          <ErrorNote error={new Error(`${save.error ? 'Dokument nicht gespeichert.' : 'Dokument nicht entfernt.'} ${errorMessage(failed)}`)} />
        </ActionNote>
      ) : null}
      <div hidden={!active} className="space-y-4">
        {docs.length ? (
          <ul className="divide-y divide-[var(--border-default)] border border-[var(--border-default)] bg-white">
            {docs.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <span className="ad-badge bg-steel-100">{DOCUMENT_KIND_LABEL[d.kind]}</span>
                {d.fileUrl && d.contentType && isViewableFile(d.contentType) ? (
                  <button
                    type="button"
                    className="ad-link cursor-pointer text-left font-semibold"
                    onClick={() => setView({ url: d.fileUrl!, filename: d.filename ?? d.title, contentType: d.contentType! })}
                  >
                    {d.title}
                  </button>
                ) : d.fileUrl ? (
                  <a href={downloadUrl(d.fileUrl)} className="ad-link font-semibold">
                    {d.title}
                  </a>
                ) : (
                  <a href={d.url ?? '#'} target="_blank" rel="noreferrer" className="ad-link font-semibold">
                    {d.title}
                  </a>
                )}
                <span className="text-[13px] text-[var(--text-muted)]">{d.url ? 'Verweis' : 'Datei'} · {fmtDate(d.validFrom ?? d.createdAt)}</span>
                {canEdit && d.kind !== 'inspection_report' ? (
                  <button
                    type="button"
                    className="ml-auto cursor-pointer text-[13px] text-arnold-red"
                    disabled={remove.busy}
                    onClick={() => {
                      if (!window.confirm('Dokument entfernen?')) return;
                      setRemoving(d.id);
                      void remove.run(async () => {
                        await deleteJson(`/api/machines/${machineId}/docs/${d.id}`);
                        await onChange();
                      });
                    }}
                  >
                    Entfernen
                  </button>
                ) : null}
                {remove.error && removing === d.id ? (
                  <div className="w-full">
                    <ErrorNote error={remove.error} />
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState title="Keine Dokumente" />
        )}
        {canEdit ? (
          <div className="ad-card space-y-3">
            <h3 className="ad-section-title">Dokument hinzufügen</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <Select label="Art" value={kind} onChange={(v) => setKind(v as DocumentKind)} options={DOCUMENT_KIND.map((k) => ({ value: k, label: DOCUMENT_KIND_LABEL[k] }))} />
              <Input label="Titel" value={title} onChange={setTitle} />
            </div>
            <Input label="Verweis (SharePoint, Herstellerportal)" value={url} onChange={setUrl} placeholder="https://…" />
            {save.error ? (
              <ActionNote revealOn={active} className={BELOW_HEADER}>
                <ErrorNote error={save.error} />
              </ActionNote>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" disabled={save.busy || !url.startsWith('http') || title.length < 2} onClick={() => void addLink()}>
                Verweis speichern
              </Button>
              <Button size="sm" variant="outline" disabled={save.busy} onClick={() => fileInput.current?.click()}>
                {uploading ? 'Wird hochgeladen …' : `Datei hochladen (PDF/JPG/PNG, ${limitInMb(LIMITS.documentMaxBytes)})`}
              </Button>
              <input
                ref={fileInput}
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                className="hidden"
                onChange={(e) => {
                  void addFile(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </div>
            {kind === 'inspection_report' ? <p className="text-[13px] text-[var(--text-muted)]">Prüfberichte bleiben, solange die Maschine existiert (§ 14 Abs. 7 BetrSichV).</p> : null}
          </div>
        ) : null}
        <FileViewer file={view} onClose={() => setView(null)} />
      </div>
    </>
  );
}
